import { useLocalization } from '@magicdoor/ui';
import { createMemo, Show, createSignal } from 'solid-js';
import CloseIcon from '~/assets/images/quick-link/close-icon.svg?component-solid';
import LinkIcon from '~/assets/images/quick-link/link-icon.svg?component-solid';
import { Button, LinkButton } from '~/components/common/Buttons';
import Tooltip from '~/components/common/Tooltip';
import { useMenu } from '~/components/layouts/Menu/MenuContext';
import { TOOLTIP_BG_COLOR } from '~/components/layouts/Menu/components/config';
import { Modal } from '~/components/modals/Modal';
import { IconChevronRight } from '~/components/ui';
import { useCompanies } from '~/contexts/global';
import { useDeviceDetect } from '~/hooks/usedeviceDetect';
import { QuickLinksPanel } from './QuickLinksPanel';

export const LinkToWebsite = (props: { isMenuCollapsed?: boolean }) => {
  const [hoverTimeout, setHoverTimeout] = createSignal<number>();
  const [showPanel, setShowPanel] = createSignal(false);
  const { getWebsiteUrl, companies } = useCompanies();
  const { isMobileView } = useDeviceDetect();
  const [showQuickLinks, setShowQuickLinks] = createSignal(false);
  const { t } = useLocalization();
  const websiteUrl = createMemo(() => getWebsiteUrl('/'));
  const menu = useMenu();
  const { setIsSidebarOpen } = menu!;

  const quickLinks = createMemo(() => {
    const websiteUrl = getWebsiteUrl('/');
    return [
      { name: t('Company website'), href: websiteUrl },
      { name: t('Tenant portal'), href: websiteUrl + 'tenants' },
      { name: t('Owner portal'), href: websiteUrl + 'owners' },
    ];
  });

  const handleLinkClick = () => {
    if (isMobileView()) {
      setIsSidebarOpen(false);
      setShowQuickLinks(!showQuickLinks());
    }
  };

  const handleMouseLeave = () => {
    clearTimeout(hoverTimeout());
    const timeout = setTimeout(() => setShowPanel(false), 300);
    setHoverTimeout(timeout as unknown as number);
  };

  return (
    <div class="m-4 flex flex-col items-stretch justify-center gap-4 border-t border-white/40 pt-4">
      <Show
        when={!props.isMenuCollapsed}
        fallback={
          <Tooltip bgColor={TOOLTIP_BG_COLOR} class="w-full" align="right" message={t('Quick links')}>
            <LinkButton
              href={websiteUrl()}
              variant="outlined"
              class="flex size-full justify-center border-white/40 px-0 text-white"
              target="_blank">
              <LinkIcon class="size-5" />
            </LinkButton>
          </Tooltip>
        }>
        <div
          class="group relative inline-block"
          onMouseEnter={() => !isMobileView() && setShowPanel(true)}
          onMouseLeave={() => !isMobileView() && handleMouseLeave()}>
          <Button variant="outlined" class="flex w-full justify-center border-white/40 p-2 text-white" onClick={handleLinkClick}>
            <div class="flex w-40 items-center gap-x-2">
              <LinkIcon class="size-5" />
              {t('Quick links')}
            </div>
            <IconChevronRight class="size-5" />
          </Button>
          <Show when={!isMobileView()}>
            <div
              class="absolute bottom-0 left-full z-50 hidden rounded-lg pl-6 opacity-0 transition-all duration-200 group-hover:opacity-100 hover-allowed:group-hover:block"
              classList={{
                'hidden opacity-0': !showPanel(),
                'block opacity-100': showPanel(),
              }}
              onMouseEnter={() => setShowPanel(true)}
              onMouseLeave={() => handleMouseLeave()}>
              <QuickLinksPanel quickLinks={quickLinks()} />
            </div>
          </Show>
        </div>

        <Show when={companies()}>
          <div class="flex flex-col items-center justify-center gap-2 text-sm text-white">
            <p class="text-xs text-white/80">{t('Company phone number')}:</p>
            <p>{companies()?.phoneNumber || t(`Not selected yet`)}</p>
          </div>
        </Show>

        <Modal visible={showQuickLinks()} showFooter={false}>
          <div class="relative">
            <CloseIcon class="absolute right-1 top-1 z-10 cursor-pointer" onClick={() => setShowQuickLinks(false)} />
            <QuickLinksPanel quickLinks={quickLinks()} />
          </div>
        </Modal>
      </Show>
    </div>
  );
};
